import * as React from "react";
import { Client, ClientService } from "@app/client";
import { BreadcrumbTemplateEnum, Breadcrumbs, LoadingSpinner, Page, PageHeader, PageHeaderRight, PageInner, PageSection } from "@app/shared";
import { QESAlert, QESButton, QESSelect, QESTextInput } from "@app/shared/qes-standards";
import { ColumnConfig, SortDirections, Waffles, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Link, Redirect } from "react-router-dom";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import { orderBy } from "lodash";
import { ProjectService } from "@app/projects";
import moment from "moment";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { JSX } from "react";

interface ClientsTableData {
  clientName: JSX.Element;
  //numberOfUsers: number;
  status: string;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  clientName: {
    displayText: "Organization",
    testId: "client-name",
  },
  status: {
    displayText: "Status",
    testId: "status",
  },
};

const take = 15;
export const ClientList: React.FC<React.PropsWithChildren<unknown>> = () => {
  const projectService = new ProjectService();

  const [clients, setClients] = React.useState<Client[]>([]);
  const [redirect, setRedirect] = React.useState("");
  const [clientsTableData, setClientsTableData] = React.useState<ClientsTableData[]>([]);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const urlParams = new URLSearchParams(window.location.search);
  const [orgFilter, setOrgFilter] = React.useState(urlParams.get("org") ?? "");
  const [skip, setSkip] = React.useState(0);
  //const [selectedClients, setSelectedClients] = React.useState<string[]>([]);
  const [statusFilter, setStatusFilter] = React.useState(urlParams.get("stat") ?? "");
  const user = React.useContext(UserContext);
  const userIsQuestAdmin = React.useRef(user.roles.includes(Roles.QUEST_ADMIN)).current;
  const [exportingProjectConfigs, setExportingProjectConfigs] = React.useState(false);

  React.useEffect(() => {
    // fetch clients
    const clientService = new ClientService();
    if (userIsQuestAdmin) {
      clientService.getAllClients().then(fetchQuestAdminClientsResolve, fetchClientsReject);
    } else {
      // for client admin user, just redirect to the client details page for their client 
      setRedirect(`/admin/client-administration/${user.clientId.toString()}`);
      //clientService.getClient(user.clientId).then(fetchClientAdminClientsResolve, fetchClientsReject);
    }
  }, []);

  const fetchQuestAdminClientsResolve = (clients: Client[]) => {
    setClients(clients);
    setClientsTableData(buildTableData(clients));
    setLoading(false);
  }

  //const fetchClientAdminClientsResolve = (client: Client) => {
  //  setClients([client]);
  //  setClientsTableData(buildTableData([client]));
  //  setLoading(false);
  //}

  const fetchClientsReject = () => {
    setError(`There was a problem retrieving your client${userIsQuestAdmin ? "s" : ""}.`);
    setLoading(false);
  }

  const filterTable = (clearFilter?: boolean) => {
    setSkip(0);
    setClientsTableData(buildTableData(clients, clearFilter));

    const searchParams = new URLSearchParams(window.location.search);

    if (!clearFilter && !!orgFilter) {
      searchParams.set("org", orgFilter);
    } else {
      searchParams.delete("org");
    }
    if (!clearFilter && !!statusFilter) {
      searchParams.set("stat", statusFilter);
    } else {
      searchParams.delete("stat");
    }

    history.replaceState(null, "", window.location.pathname + "?" + searchParams.toString());
  };

  const buildTableData = (data: Client[], clearFilter?: boolean): ClientsTableData[] => {
    let filteredData = data;
    if (!clearFilter) {
      if (orgFilter) {
        filteredData = filteredData.filter((d) => d.name.toLowerCase().includes(orgFilter.toLowerCase()));
      }
      if (statusFilter) {
        filteredData = filteredData.filter((d) => (d.isActive && statusFilter.toLowerCase() === "active") || (!d.isActive && statusFilter.toLowerCase() === "inactive"));
      }
    }

    return filteredData.map<ClientsTableData>((client) => {
      /* eslint-disable sort-keys */
      return {
        clientName: <Link key={client.name} to={`/admin/client-administration/${client.clientId.toString()}`}>{client.name}</Link>,
        //numberOfUsers: 0,
        status: client.isActive ? "Active" : "Inactive",
        [wafflesKeyColumnName]: client.clientId.toString(),
      };
      /* eslint-enable sort-keys */
    });
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => setSkip((pageNumber - 1) * take),
    recordsPerPage: take,
    skip: skip,
    totalRecords: clientsTableData.length,
  }

  //const bulkActionBar = React.useMemo(() => {
  //  const isDisabled = clients.filter((client) => selectedClients.includes(client.clientId.toString()) && client.isActive).length > 0;
  //  return (
  //    <QESButton
  //      disabled={isDisabled}
  //      onClick={}
  //      qesTestId="clients-admin__deactivate"
  //      qesType="secondary"
  //    >
  //      Deactivate
  //    </QESButton>
  //  )
  //}, [selectedClients.length]);

  //const handleDeactivate = (): void => {

  //}

  //const updateSelectedClients = (selectedKeys: string[]): void => {
  //  setSelectedClients(selectedKeys);
  //}

  const customSort = (records: ClientsTableData[], key: keyof ClientsTableData, sortDirection: SortDirections) => {
    // ignore records because they're undefined due to passThroughSorting/we're only sending a fraction of them at a time
    // and in this case SortDirections.None orders them alphabetically anyway, so we can just sort "asc"
    let sortedData: ClientsTableData[] = [];
    switch (key) {
      case "clientName":
        sortedData = orderBy(clientsTableData, (d) => d[key].key.toString().toLowerCase(), sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
      case "status":
        sortedData = orderBy(clientsTableData, (d) => d[key], sortDirection === SortDirections.Desc ? "desc" : "asc");
        break;
    }
    setClientsTableData(sortedData);
    return records;
  }

  const handleExportProjectConfigsAll = async () => {
    setExportingProjectConfigs(true);
    await projectService.getProjectDetailsCsv(`Project Configurations(All)_${moment.utc().local().format("yyyyMMDD_hhmmss")}`);
    setExportingProjectConfigs(false);
  }

  return (
    <>
      {redirect && <Redirect to={redirect} />}
      {!redirect && (
        <Page documentTitle="Clients - Admin">
          <Breadcrumbs template={BreadcrumbTemplateEnum.ClientAdministration} items={[]} />
          <PageInner>
            <PageHeader title={"Clients"} iconName={QesIcons.FontAwesome.Bank} disableHR={false}>
              <PageHeaderRight>
                <RoleRestricted allowedRoles={[Roles.QUEST_ADMIN]}>
                  <QESButton
                    qesTestId="export-project-configs-all"
                    qesType="secondary"
                    onClick={() => handleExportProjectConfigsAll()}
                    disabled={exportingProjectConfigs}
                    className="clients-admin-export-btn"
                  >
                    Export Project Configuration (All Clients)
                  </QESButton>
                  <Link
                    className="qes-button qes-button-secondary"
                    to="/admin/client-administration/client-default-roles"
                  >
                    Edit Default Roles for New Clients
                  </Link>
                  <Link
                    className="qes-button qes-button-secondary"
                    data-testid="client-details__role-assignments"
                    to={{
                      pathname: "/admin/internal-reports/role-assignments",
                    }}
                  >
                    Role Assignments
                  </Link>
                  <Link
                    className="qes-button qes-button-primary"
                    to="/admin/client-administration/create"
                  >
                    Add New Client
                  </Link>
                </RoleRestricted>
              </PageHeaderRight>
            </PageHeader>
            <PageSection>
              {!!error && <QESAlert type="danger" persist={true}>{error}</QESAlert>}
              {loading && <LoadingSpinner />}
              {!loading && clients.length === 0 && <p>You don&apos;t seem to have any clients available.</p>}
              {!loading && clients.length > 0 && (
                <div>
                  <div className="clients-admin-filters row mb-3 mt-4">
                    <QESTextInput
                      containerClassName="clients-admin-filters__organization"
                      testId="clients-admin__organization-filter"
                      label="Organization"
                      onChange={(e) => setOrgFilter(e.target.value)}
                      onKeyPress={(e) => { if (e.key.toLowerCase() === "enter") filterTable(); }}
                      value={orgFilter}
                    />
                    <QESSelect
                      allowEmpty={true}
                      containerClassName="clients-admin-filters__input"
                      testId="clients-admin__status-filter"
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                      value={statusFilter}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </QESSelect>
                    <QESButton
                      qesType="primary"
                      qesTestId="clients-admin__apply-filters"
                      onClick={() => { filterTable(); }}
                    >
                      Filter
                    </QESButton>
                    <QESButton
                      qesType="secondary"
                      qesTestId="clients-admin__clear-filters"
                      onClick={() => { setStatusFilter(""); setOrgFilter(""); filterTable(true); }}
                      disabled={!statusFilter && !orgFilter}
                    >
                      Clear
                    </QESButton>
                  </div>
                  <Waffles<ClientsTableData>
                    //bulkActionBar={bulkActionBar}
                    columns={columnConfig}
                    customSort={customSort}
                    data={clientsTableData.slice(skip, skip + take)}
                    enablePagination={true}
                    fullWidth={true}
                    paginationProps={paginationProps}
                    passThroughSorting={true}
                    //handleCheckboxChange={updateSelectedClients}
                    //selectedKeys={selectedClients}
                    //showCheckboxes={true}
                    testId="clients-table"
                  />
                </div>
              )}
            </PageSection>
          </PageInner>
        </Page >
      )}
    </>
  );
}